import { Dialog, DialogTitle, DialogContent, DialogActions } from "@mui/material";
import { PropsWithChildren, useCallback } from "react";

import PrimaryButton from "./primary_button";
import ProgressBar from "./progress_bar";
import DynamicIcon from "./dynamic_icon";

/**
 * CustomModalLoading
 */

const CustomModalLoading = ({
  loading,
  message,
}: { loading: boolean, message?: string }) => {
  return (
    <>
      {
        loading &&
        <div className="absolute size-full z-50 top-0 left-0 flex justify-center items-center">
          <div className="flex-col w-48 p-4 rounded-lg gap-2 border border-stone-500 flex justify-center items-center bg-[#FFFFFF] dark:bg-[#393939]">
            <div className="w-24">
              <img src="/images/mini-logo.png" alt="logo gdol" />
            </div>
            <div className="w-full">
              <ProgressBar />
              <p className="text-black dark:text-white text-center">{message ?? "Salvando"}</p>
            </div>
          </div>
        </div>
      }
    </>
  );
}

/**
 * BaseFormControlledModal
 */

type Props = {
  open: boolean;
  saving: boolean;
  loading: boolean;
  title: string;
  width?: string;
  readOnly?: boolean;
  cancelButtonLabel?: string;
  confirmButtonLabel?: string;
  confirmSavingButtonLabel?: string;
  confirmButtonWidth?: number | string;
  savingLabel?: string;
  loadingLabel?: string;
  buttonColor?: "primary-green" | "white" | "secondary" | "secondary-white" | "yellow" | "red";
  leftButtonSlot?: React.ReactNode,
  invertButtons?: boolean;
  onClose: () => void;
  onSubmit: (e: any) => void;
};

export const BaseFormControlledModal = ({
  open,
  saving,
  loading,
  title,
  width,
  readOnly,
  cancelButtonLabel,
  confirmButtonLabel,
  confirmSavingButtonLabel,
  confirmButtonWidth,
  savingLabel,
  loadingLabel,
  buttonColor,
  leftButtonSlot,
  invertButtons,
  children,
  onClose,
  onSubmit,
}: PropsWithChildren<Props>) => {

  const handleSubmit = useCallback((e: any) => {
    try {
      e.preventDefault();
      if (!saving && !loading && !readOnly) onSubmit(e);
    } catch (e: unknown) {
    }
  }, [
    saving,
    loading,
    readOnly,
    onSubmit
  ]);

  const handleClose = useCallback(() => {
    if (!saving && !loading) onClose();
  }, [
    saving,
    loading,
    onClose
  ]);

  return (
    <Dialog
      open={open}
      slotProps={{
        paper: {
          component: 'form',
          onSubmit: handleSubmit,
        }
      }}
      sx={{
        "& .MuiDialog-container": {
          position: 'relative',

          "& .MuiPaper-root": {
            width: "100%",
            maxWidth: width ?? "60rem",
          },
          "& .MuiFormControlLabel-label": {
            fontStyle: 'normal',
            fontWeight: 400,
            fontSize: '13px',
          },
          "& .MuiCheckbox-root": {
            padding: '.25rem',
          },
        },
      }}>
      <DialogTitle component='div' sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }}>
        <h2>{title}</h2>
        <div
          onClick={e => handleClose()}>
          <DynamicIcon
            nameIcon="Close"
            size={25}
            className="text-black dark:text-white cursor-pointer"
          />
        </div>
      </DialogTitle>

      <DialogContent>
        {children}
      </DialogContent>

      <DialogActions>
        {(invertButtons === true) ? (
          <div className="w-full h-full items-center justify-between flex p-4 gap-4">
            <div className="flex flex-row gap-2">
              <div className="p-2 hover:bg-hover-light-gray cursor-pointer dark:hover:bg-border-dark-gray rounded-[4px] font-semibold">
                <h1
                  className="text-sm text-black dark:text-white"
                  onClick={handleClose}>
                  {cancelButtonLabel ?? 'Cancelar'}
                </h1>
              </div>

              <div className={`${confirmButtonWidth ?? 'w-[10rem]'}`}>
                {saving ? (
                  <PrimaryButton
                    buttonColor={buttonColor ?? "primary-green"}>
                    {confirmSavingButtonLabel ?? 'Salvando...'}
                  </PrimaryButton>
                ) : (
                  <PrimaryButton
                    type="submit"
                    disabled={readOnly}
                    buttonColor={buttonColor ?? "primary-green"}>
                    {confirmButtonLabel ?? 'Salvar'}
                  </PrimaryButton>
                )}
              </div>
            </div>

            <div className="w-auto flex flex-row">
              {leftButtonSlot}
            </div>
          </div>
        ) : (
          <div className="w-full h-full items-center justify-between flex p-4 gap-4">
            <div className="w-auto flex flex-row">
              {leftButtonSlot}
            </div>

            <div className="flex flex-row gap-2">
              <div className="p-2 hover:bg-hover-light-gray cursor-pointer dark:hover:bg-border-dark-gray rounded-[4px] font-semibold">
                <h1
                  className="text-sm text-black dark:text-white"
                  onClick={handleClose}>
                  {cancelButtonLabel ?? 'Cancelar'}
                </h1>
              </div>

              <div className={`${confirmButtonWidth ?? 'w-[10rem]'}`}>
                {saving ? (
                  <PrimaryButton
                    buttonColor={buttonColor ?? "primary-green"}>
                    {confirmSavingButtonLabel ?? 'Salvando...'}
                  </PrimaryButton>
                ) : (
                  <PrimaryButton
                    type="submit"
                    disabled={readOnly}
                    buttonColor={buttonColor ?? "primary-green"}>
                    {confirmButtonLabel ?? 'Salvar'}
                  </PrimaryButton>
                )}
              </div>
            </div>
          </div>
        )}

      </DialogActions>

      <CustomModalLoading
        loading={saving || loading}
        message={saving ? (savingLabel ?? 'Salvando...') : (loading ? (loadingLabel ?? 'Carregando...') : '')} />

    </Dialog>
  );
}